import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Jobs_API, Category_API, Admin_API, Hr_API } from "./environment";
import { useAuth } from "../context/AuthContext";
import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaFilter,
  FaSave,
  FaTimes,
} from "react-icons/fa";
import { GrDocumentText } from "react-icons/gr";
import { TbWorldShare, TbWorldOff } from "react-icons/tb";
import Swal from "sweetalert2";
import Pagination from "../pages/Pagination";
import "./JobModal.css";

// Component for managing jobs with add, edit, delete, multiple delete, filter, and detail view functionality
export default function Jobs() {
  const { userData } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [hrs, setHrs] = useState([]);
  const [adminIdToUsername, setAdminIdToUsername] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [filters, setFilters] = useState({
    req_id: [],
    jobName: [],
    category: [],
    workMode: [],
    created_by: [],
  });
  const [showFilterPopup, setShowFilterPopup] = useState(null);
  const [tempSelected, setTempSelected] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("view"); // "view", "edit", "add"
  const [activeTab, setActiveTab] = useState("active"); // "active" or "inactive"

  const columnDisplay = {
    sr_no: "Sr. No.",
    req_id: "Req ID",
    jobName: "Job Name",
    category: "Category",
    workMode: "Work Mode",
    created_by: "Created By",
  };

  // Fetch all categories, admins, and hrs on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories
        const catResponse = await fetch(`${Category_API}/getAll`, {
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
          },
        });
        if (!catResponse.ok) {
          throw new Error("Failed to fetch categories");
        }
        const catData = await catResponse.json();
        setCategories(catData);

        // Fetch admins
        const adminResponse = await fetch(`${Admin_API}/getAll`, {
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
          },
        });
        if (!adminResponse.ok) {
          throw new Error("Failed to fetch admins");
        }
        const adminData = await adminResponse.json();
        const adminMap = {};
        adminData.forEach((admin) => {
          adminMap[admin.id] = admin.username;
        });
        setAdminIdToUsername(adminMap);

        // Fetch hrs
        const hrResponse = await fetch(`${Hr_API}/getAll`, {
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
          },
        });
        if (!hrResponse.ok) {
          throw new Error("Failed to fetch hrs");
        }
        const hrData = await hrResponse.json();
        setHrs(hrData);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [userData.access_token]);

  // Fetch all jobs on component mount
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await fetch(`${Jobs_API}/getAll`, {
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
            Accept: "application/json",
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch jobs");
        }
        const data = await response.json();
        // Ensure hr and locations fields are arrays, and status is boolean
        const normalizedData = data.map((job) => ({
          ...job,
          hr: Array.isArray(job.hr) ? job.hr : [],
          locations: Array.isArray(job.locations) ? job.locations : [],
          status: job.status === true || job.status === false ? job.status : false,
        }));
        setJobs(normalizedData);
      } catch (error) {
        console.error("Error fetching jobs:", error);
      }
    };
    fetchJobs();
  }, [userData.access_token]);

  // Get distinct values for a column
  const getDistinctValues = (column) => {
    const values = jobs.map((item) => item[column]);
    const unique = [...new Set(values)];
    return unique.sort((a, b) =>
      column === "created_by"
        ? a - b
        : a?.toString().localeCompare(b?.toString())
    );
  };

  // Apply filters to an item
  const applyFilters = (item) => {
    return (
      (filters.req_id.length === 0 || filters.req_id.includes(item.req_id)) &&
      (filters.jobName.length === 0 ||
        filters.jobName.includes(item.jobName)) &&
      (filters.category.length === 0 ||
        filters.category.includes(item.category)) &&
      (filters.workMode.length === 0 ||
        filters.workMode.includes(item.workMode)) &&
      (filters.created_by.length === 0 ||
        filters.created_by.includes(item.created_by))
    );
  };

  // Filter jobs by tab and filters
  const tabFilteredJobs = jobs.filter((job) => job.status === (activeTab === "active"));
  const filteredJobs = tabFilteredJobs.filter(applyFilters);

  const totalPages = Math.ceil(filteredJobs.length / itemsPerPage);
  const paginatedJobs = filteredJobs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const hasActiveFilters = Object.values(filters).some((arr) => arr.length > 0);

  // Toggle row selection
  const toggleRowSelection = (job_id) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(job_id)
        ? prevSelected.filter((rowId) => rowId !== job_id)
        : [...prevSelected, job_id]
    );
  };

  // Select all filtered jobs
  const handleSelectAll = () => {
    if (selectedRows.length === filteredJobs.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredJobs.map((item) => item.job_id));
    }
  };

  // Handle filter click for a column
  const handleFilterClick = (column) => {
    if (column === "sr_no") return; // No filter for Sr. No.
    setShowFilterPopup(column);
    setTempSelected(filters[column] || []);
    setSearchInput("");
  };

  // Apply filter selections
  const applyFilter = () => {
    const distinct = getDistinctValues(showFilterPopup);
    const newSelected =
      tempSelected.length === distinct.length ? [] : tempSelected;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [showFilterPopup]: newSelected,
    }));
    setCurrentPage(1);
    setShowFilterPopup(null);
  };

  // Delete multiple selected rows
  const deleteSelectedRows = async () => {
    if (selectedRows.length === 0) return;

    Swal.fire({
      text: `Do you want to delete ${selectedRows.length} selected jobs?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await Promise.all(
            selectedRows.map(async (job_id) => {
              const response = await fetch(`${Jobs_API}/delete/${job_id}`, {
                method: "DELETE",
                headers: {
                  Authorization: `Bearer ${userData.access_token}`,
                  Accept: "application/json",
                },
              });
              if (!response.ok) {
                throw new Error(`Failed to delete job ${job_id}`);
              }
            })
          );
          const updatedJobs = jobs.filter(
            (job) => !selectedRows.includes(job.job_id)
          );
          setJobs(updatedJobs);
          setSelectedRows([]);
          Swal.fire(
            "Success!",
            "Selected jobs deleted successfully",
            "success"
          );
        } catch (error) {
          console.error("Error deleting jobs:", error);
          Swal.fire(
            "Error",
            "Failed to delete some jobs. Please try again.",
            "error"
          );
        }
      }
    });
  };

  // Generate next req_id
  const generateReqId = () => {
    const maxReqId = jobs
      .filter((job) => job.req_id && job.req_id.startsWith("REQ_"))
      .reduce((max, job) => {
        const num = parseInt(job.req_id.replace("REQ_", ""), 10);
        return isNaN(num) ? max : Math.max(max, num);
      }, 0);
    const nextNumber = maxReqId + 1;
    return `REQ_${String(nextNumber).padStart(6, "0")}`;
  };

  // Function to add a new job
  const addJob = () => {
    setSelectedJob({
      jobName: "",
      category: "",
      tags: [],
      locations: [],
      created_date: new Date().toISOString().split("T")[0],
      workMode: "",
      description: "",
      requirement: [],
      niceTOHave: [],
      weOffer: [],
      hr: [],
      created_by: userData.id,
      status: true, // New jobs are active by default
    });
    setModalMode("edit"); // Start in edit mode for adding
    setShowModal(true);
  };

  // Function to edit a job (view first, then edit)
  const viewJob = (job) => {
    setSelectedJob({
      ...job,
      hr: Array.isArray(job.hr) ? job.hr : [],
      locations: Array.isArray(job.locations) ? job.locations : [],
    });
    setModalMode("view");
    setShowModal(true);
  };

  // Toggle job status
  const toggleJobStatus = async (job) => {
    const newStatus = !job.status;
    const confirmText = newStatus
      ? "Do you want to activate this job?"
      : "Do you want to deactivate this job?";

    Swal.fire({
      text: confirmText,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const payload = {
            ...job,
            status: newStatus,
          };

          const response = await fetch(`${Jobs_API}/update/${job.job_id}`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userData.access_token}`,
              Accept: "application/json",
            },
            body: JSON.stringify(payload),
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to update job status: ${errorText}`);
          }

          const updatedJob = await response.json();
          setJobs(jobs.map((j) => (j.job_id === updatedJob.job_id ? updatedJob : j)));
          Swal.fire("Success!", `Job has been ${newStatus ? "activated" : "deactivated"}.`, "success");
        } catch (error) {
          console.error("Error updating job status:", error);
          Swal.fire("Error", "Failed to update job status.", "error");
        }
      }
    });
  };

  // Handle save (add or update)
  const handleSave = async (updatedJob) => {
    // Prepare payload matching backend's expected structure
    const payload = {
      req_id: updatedJob.job_id ? updatedJob.req_id : generateReqId(),
      jobName: updatedJob.jobName,
      category: updatedJob.category,
      tags: updatedJob.tags,
      locations: updatedJob.locations,
      created_date: updatedJob.created_date,
      workMode: updatedJob.workMode,
      description: updatedJob.description,
      requirement: updatedJob.requirement,
      niceTOHave: updatedJob.niceTOHave,
      weOffer: updatedJob.weOffer,
      hr: updatedJob.hr,
      created_by: updatedJob.created_by,
      status: updatedJob.status ?? true,
    };

    try {
      let response;
      if (updatedJob.job_id) {
        // Update existing job
        response = await fetch(`${Jobs_API}/update/${updatedJob.job_id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData.access_token}`,
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        });
      } else {
        // Add new job
        response = await fetch(`${Jobs_API}/create`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData.access_token}`,
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        });
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save job: ${errorText}`);
      }

      const savedJob = await response.json();
      if (updatedJob.job_id) {
        setJobs(
          jobs.map((job) => (job.job_id === savedJob.job_id ? savedJob : job))
        );
      } else {
        setJobs([...jobs, savedJob]);
      }
      setShowModal(false);
      Swal.fire("Success!", "Job saved successfully.", "success");
    } catch (error) {
      console.error("Error saving job:", error);
      Swal.fire("Error", `Failed to save job: ${error.message}`, "error");
    }
  };

  // Function to delete a single job
  const deleteJob = async (job_id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${Jobs_API}/delete/${job_id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to delete job");
        }

        setJobs(jobs.filter((job) => job.job_id !== job_id));
        Swal.fire("Deleted!", "Job has been deleted.", "success");
      } catch (error) {
        console.error("Error deleting job:", error);
        Swal.fire("Error", "Failed to delete job. Please try again.", "error");
      }
    }
  };

  // Render filter popup for a column
  const renderFilterPopup = (column) => {
    if (column === "sr_no") return null;
    const displayName = columnDisplay[column];
    const distinctValues = getDistinctValues(column);
    const filteredValues = distinctValues.filter(
      (val) =>
        val != null &&
        val.toString().toLowerCase().includes(searchInput.toLowerCase())
    );
    const allVisibleSelected = filteredValues.every((val) =>
      tempSelected.includes(val)
    );

    const handleToggleVisible = (e) => {
      if (e.target.checked) {
        setTempSelected([...new Set([...tempSelected, ...filteredValues])]);
      } else {
        setTempSelected(
          tempSelected.filter((val) => !filteredValues.includes(val))
        );
      }
    };

    const clearSelections = () => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        [column]: [],
      }));
      setTempSelected([]);
      setShowFilterPopup(null);
    };

    // For created_by, show username instead of id
    const getDisplayValue = (val) => {
      if (column === "created_by") {
        return adminIdToUsername[val] || "Unknown";
      }
      return val != null ? val.toString() : "N/A";
    };

    return (
      <div className="filter-popup">
        <div className="filter-header">
          <span className="column-name">{displayName}</span>
          <span className="clear-filter" onClick={clearSelections}>
            Clear
          </span>
        </div>
        <div className="filter-search-container">
          <input
            type="checkbox"
            checked={allVisibleSelected}
            onChange={handleToggleVisible}
          />
          <input
            className="filter-search"
            type="text"
            placeholder="Search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <span className="clear-search" onClick={() => setSearchInput("")}>
              ×
            </span>
          )}
        </div>
        <div className="suggestions-list">
          {filteredValues.length === 0 ? (
            <div>No results found</div>
          ) : (
            filteredValues.map((val) => (
              <label key={val?.toString() || "null"}>
                <input
                  type="checkbox"
                  value={val}
                  checked={tempSelected.includes(val)}
                  onChange={(e) => {
                    const value =
                      column === "created_by"
                        ? Number(e.target.value)
                        : e.target.value;
                    setTempSelected((prev) =>
                      e.target.checked
                        ? [...prev, value]
                        : prev.filter((v) => v !== value)
                    );
                  }}
                />
                {getDisplayValue(val)}
              </label>
            ))
          )}
        </div>
        <div className="filter-buttons">
          <button onClick={() => setShowFilterPopup(null)}>Cancel</button>
          <button onClick={applyFilter}>Apply</button>
        </div>
      </div>
    );
  };

  return (
    <div className="relative">
      <Helmet>
        <title>Jobs - SMTS Admin Panel</title>
      </Helmet>

      <div className="page-title">
        <h2
          className={activeTab === "active" ? "active" : ""}
          onClick={() => {
            setActiveTab("active");
            setCurrentPage(1);
            setSelectedRows([]);
          }}
          style={{
            cursor: "pointer",
            display: "inline-block",
            marginRight: "20px",
          }}
        >
          Active Jobs
        </h2>
        <h2
          className={activeTab === "inactive" ? "active" : ""}
          onClick={() => {
            setActiveTab("inactive");
            setCurrentPage(1);
            setSelectedRows([]);
          }}
          style={{ cursor: "pointer", display: "inline-block" }}
        >
          Inactive Jobs
        </h2>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th className="hide-column">
                <input
                  type="checkbox"
                  checked={
                    selectedRows.length === filteredJobs.length &&
                    filteredJobs.length > 0
                  }
                  onChange={handleSelectAll}
                />
              </th>
              <th style={{ position: "relative" }}>
                {hasActiveFilters && (
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.length === filteredJobs.length &&
                      filteredJobs.length > 0
                    }
                    onChange={handleSelectAll}
                  />
                )}
                {columnDisplay.sr_no}
              </th>
              <th style={{ position: "relative" }}>
                {columnDisplay.req_id}
                <FaFilter
                  className={`filter-icon ${
                    filters.req_id.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("req_id")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "req_id" && renderFilterPopup("req_id")}
              </th>
              <th style={{ position: "relative" }}>
                {columnDisplay.jobName}
                <FaFilter
                  className={`filter-icon ${
                    filters.jobName.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("jobName")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "jobName" && renderFilterPopup("jobName")}
              </th>
              <th style={{ position: "relative" }}>
                {columnDisplay.category}
                <FaFilter
                  className={`filter-icon ${
                    filters.category.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("category")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "category" &&
                  renderFilterPopup("category")}
              </th>
              <th style={{ position: "relative" }}>
                {columnDisplay.created_by}
                <FaFilter
                  className={`filter-icon ${
                    filters.created_by.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("created_by")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "created_by" &&
                  renderFilterPopup("created_by")}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedJobs.map((job, index) => (
              <tr
                key={job.job_id}
                className={selectedRows.includes(job.job_id) ? "selected" : ""}
                onDoubleClick={() => toggleRowSelection(job.job_id)}
              >
                <td className="hide-column">
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(job.job_id)}
                    onChange={() => toggleRowSelection(job.job_id)}
                  />
                </td>
                <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                <td>{job.req_id}</td>
                <td>{job.jobName}</td>
                <td>{job.category}</td>
                <td>{adminIdToUsername[job.created_by] || "Unknown"}</td>
                <td className="actions">
                  <button
                    className={`action-button ${
                      selectedRows.length > 1 ? "disabled-icon" : ""
                    }`}
                    onClick={() => viewJob(job)}
                    disabled={selectedRows.length > 1}
                  >
                    <GrDocumentText />
                  </button>
                  <button
                    className={`action-button ${
                      selectedRows.length > 1 ? "disabled-icon" : ""
                    }`}
                    onClick={() => deleteJob(job.job_id)}
                    disabled={selectedRows.length > 1}
                  >
                    <FaTrash />
                  </button>
                  {activeTab === "inactive" && (
                    <button
                      className={`action-button ${
                        selectedRows.length > 1 ? "disabled-icon" : ""
                      }`}
                      onClick={() => toggleJobStatus(job)}
                      disabled={selectedRows.length > 1}
                      title="Activate Job"
                    >
                      <TbWorldShare />
                    </button>
                  )}
                  {activeTab === "active" && (
                    <button
                      className={`action-button ${
                        selectedRows.length > 1 ? "disabled-icon" : ""
                      }`}
                      onClick={() => toggleJobStatus(job)}
                      disabled={selectedRows.length > 1}
                      title="Deactivate Job"
                    >
                      <TbWorldOff />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        setCurrentPage={setCurrentPage}
        setItemsPerPage={(value) => {
          setCurrentPage(1);
          setItemsPerPage(value);
        }}
      />

      {selectedRows.length > 0 && (
        <button className="delete-selected" onClick={deleteSelectedRows}>
          <FaTrash className="icon" title="Delete Selected" /> (
          {selectedRows.length})
        </button>
      )}

      <button className="add-button" onClick={addJob}>
        <FaPlus />
      </button>

      {showModal && (
        <JobModal
          job={selectedJob}
          mode={modalMode}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
          setMode={setModalMode}
          categories={categories}
          hrs={hrs}
          userData={userData}
          adminIdToUsername={adminIdToUsername}
        />
      )}
    </div>
  );
}

// JobModal component remains unchanged (no status field to edit)
function JobModal({
  job,
  mode,
  onClose,
  onSave,
  setMode,
  categories,
  hrs,
  userData,
  adminIdToUsername,
}) {
  const [editJob, setEditJob] = useState({
    ...job,
    hr: Array.isArray(job.hr) ? job.hr : [],
    locations: Array.isArray(job.locations) ? job.locations : [],
  });
  const [newItem, setNewItem] = useState({});
  const [selectedHr, setSelectedHr] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");

  const isEdit = mode === "edit";
  const isAdd = !job.job_id;

  const availableLocations = ["India", "USA"];

  const hrIdToName = {};
  hrs.forEach((hr) => {
    hrIdToName[hr.hr_id] = hr.hr_name;
  });

  const handleChange = (e) => {
    setEditJob({ ...editJob, [e.target.name]: e.target.value });
  };

  const addToArray = (field) => {
    if (newItem[field] && newItem[field].trim()) {
      setEditJob({
        ...editJob,
        [field]: [...editJob[field], newItem[field].trim()],
      });
      setNewItem({ ...newItem, [field]: "" });
    }
  };

  const removeFromArray = (field, index) => {
    const updatedArray = editJob[field].filter((_, i) => i !== index);
    setEditJob({ ...editJob, [field]: updatedArray });
  };

  const addHr = () => {
    if (selectedHr) {
      const hr = hrs.find((h) => h.hr_name === selectedHr);
      if (hr && !editJob.hr.includes(hr.hr_id)) {
        setEditJob({
          ...editJob,
          hr: [...editJob.hr, hr.hr_id],
        });
        setSelectedHr("");
      }
    }
  };

  const removeHr = (index) => {
    const updatedHr = editJob.hr.filter((_, i) => i !== index);
    setEditJob({ ...editJob, hr: updatedHr });
  };

  const addLocation = () => {
    if (selectedLocation && !editJob.locations.includes(selectedLocation)) {
      setEditJob({
        ...editJob,
        locations: [...editJob.locations, selectedLocation],
      });
      setSelectedLocation("");
    }
  };

  const removeLocation = (index) => {
    const updatedLocations = editJob.locations.filter((_, i) => i !== index);
    setEditJob({ ...editJob, locations: updatedLocations });
  };

  const save = () => {
    onSave(editJob);
  };

  const startEdit = () => {
    setMode("edit");
  };

  const cancelEdit = () => {
    if (isAdd) {
      onClose();
    } else {
      setEditJob({
        ...job,
        hr: Array.isArray(job.hr) ? job.hr : [],
        locations: Array.isArray(job.locations) ? job.locations : [],
      });
      setMode("view");
    }
  };

  const renderArray = (field, label) => {
    return (
      <tr>
        <td>{label}</td>
        <td>
          {isEdit ? (
            <div>
              <ul>
                {editJob[field].map((item, index) => (
                  <li key={index}>
                    {item}
                    <button onClick={() => removeFromArray(field, index)}>
                      <FaTimes />
                    </button>
                  </li>
                ))}
              </ul>
              <input
                type="text"
                value={newItem[field] || ""}
                onChange={(e) =>
                  setNewItem({ ...newItem, [field]: e.target.value })
                }
              />
              <button onClick={() => addToArray(field)}>
                <FaPlus />
              </button>
            </div>
          ) : (
            <ul>
              {editJob[field].map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          )}
        </td>
      </tr>
    );
  };

  const renderHrArray = () => {
    return (
      <tr>
        <td>HR</td>
        <td>
          {isEdit ? (
            <div>
              <ul>
                {editJob.hr.map((hr_id, index) => (
                  <li key={index}>
                    {hrIdToName[hr_id] || hr_id}
                    <button onClick={() => removeHr(index)}>
                      <FaTimes />
                    </button>
                  </li>
                ))}
              </ul>
              <select
                value={selectedHr}
                onChange={(e) => setSelectedHr(e.target.value)}
              >
                <option value="">Select HR</option>
                {hrs.map((hr) => (
                  <option key={hr.hr_id} value={hr.hr_name}>
                    {hr.hr_name}
                  </option>
                ))}
              </select>
              <button onClick={addHr}>
                <FaPlus />
              </button>
            </div>
          ) : (
            <ul>
              {editJob.hr.map((hr_id, index) => (
                <li key={index}>{hrIdToName[hr_id] || hr_id}</li>
              ))}
            </ul>
          )}
        </td>
      </tr>
    );
  };

  const renderLocationArray = () => {
    return (
      <tr>
        <td>Locations</td>
        <td>
          {isEdit ? (
            <div>
              <ul>
                {editJob.locations.map((location, index) => (
                  <li key={index}>
                    {location}
                    <button onClick={() => removeLocation(index)}>
                      <FaTimes />
                    </button>
                  </li>
                ))}
              </ul>
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
              >
                <option value="">Select Location</option>
                {availableLocations.map((loc) => (
                  <option key={loc} value={loc}>
                    {loc}
                  </option>
                ))}
              </select>
              <button onClick={addLocation}>
                <FaPlus />
              </button>
            </div>
          ) : (
            <ul>
              {editJob.locations.map((location, index) => (
                <li key={index}>{location}</li>
              ))}
            </ul>
          )}
        </td>
      </tr>
    );
  };

  const renderReadOnlyField = (field, label) => {
    return (
      <tr>
        <td>{label}</td>
        <td>{editJob[field]}</td>
      </tr>
    );
  };

  const renderCreatedBy = () => {
    const username = adminIdToUsername[editJob.created_by] || "Unknown";
    return (
      <tr>
        <td>Created By</td>
        <td>
          {isEdit ? (
            <input
              type="text"
              name="created_by"
              value={editJob.created_by}
              readOnly
            />
          ) : (
            username
          )}
        </td>
      </tr>
    );
  };

  const renderField = (field, label, type = "text") => {
    return (
      <tr>
        <td>{label}</td>
        <td>
          {isEdit ? (
            type === "textarea" ? (
              <textarea
                name={field}
                value={editJob[field]}
                onChange={handleChange}
              />
            ) : (
              <input
                type={type}
                name={field}
                value={editJob[field]}
                onChange={handleChange}
              />
            )
          ) : (
            editJob[field]
          )}
        </td>
      </tr>
    );
  };

  const renderCategoryDropdown = () => {
    return (
      <tr>
        <td>Category</td>
        <td>
          {isEdit ? (
            <select
              name="category"
              value={editJob.category}
              onChange={handleChange}
            >
              <option value="">Select Category</option>
              {categories.map((cat) => (
                <option key={cat.cat_id} value={cat.Name}>
                  {cat.Name}
                </option>
              ))}
            </select>
          ) : (
            editJob.category
          )}
        </td>
      </tr>
    );
  };

  const renderWorkModeDropdown = () => {
    const workModes = ["Remote", "Office", "Hybrid"];
    return (
      <tr>
        <td>Work Mode</td>
        <td>
          {isEdit ? (
            <select
              name="workMode"
              value={editJob.workMode}
              onChange={handleChange}
            >
              <option value="">Select Work Mode</option>
              {workModes.map((mode) => (
                <option key={mode} value={mode}>
                  {mode}
                </option>
              ))}
            </select>
          ) : (
            editJob.workMode
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="job-modal-overlay">
      <div className="job-modal">
        <div className="job-modal-header">
          <h2>
            {isAdd ? "Add New Job" : `(${editJob.job_id}) ${editJob.jobName}`}
          </h2>
          <div className="job-modal-actions">
            {!isEdit && !isAdd && (
              <button onClick={startEdit}>
                <FaEdit />
              </button>
            )}
            {isEdit && (
              <>
                <button onClick={save}>
                  <FaSave />
                </button>
                <button onClick={cancelEdit}>
                  <FaTimes />
                </button>
              </>
            )}
            <button className="job-modal-close" onClick={onClose}>
              <FaTimes />
            </button>
          </div>
        </div>
        <table className="job-details-table">
          <tbody>
            {!isAdd && renderReadOnlyField("job_id", "Job ID")}
            {!isAdd && renderReadOnlyField("req_id", "Req ID")}
            {renderField("jobName", "Job Name")}
            {renderCategoryDropdown()}
            {renderWorkModeDropdown()}
            {renderField("created_date", "Created Date", "date")}
            {renderField("description", "Description", "textarea")}
            {renderArray("tags", "Tags")}
            {renderLocationArray()}
            {renderArray("requirement", "Requirements")}
            {renderArray("niceTOHave", "Nice to Have")}
            {renderArray("weOffer", "We Offer")}
            {renderHrArray()}
            {renderCreatedBy()}
          </tbody>
        </table>
      </div>
    </div>
  );
}